import React, { Component } from "react";
import Loading from "../../common/components/loading/loading";
import {
  getFilteredProductForMarket,
  refilterProductForMarketDateChange,
} from "../../common/services/products/productUtilities";
import { getVenue } from "../../common/services/markets/venueService";
import { getTermSchedule } from "../../common/services/markets/termScheduleService";
import HistoricalTradeDataTable from "./historicalTradeDataTable";
import { withTranslation } from "react-i18next";
import ListGroup from "react-bootstrap/ListGroup";
import Input from "../../common/components/form/input";
import { getAggregateTradesByMarket } from "../../common/services/marketdata/tradeService";

class HistoricalTradeDataHeader extends Component {
  state = {
    marketAttributes: [],
    trades: [],
    forwardTrades: [],
    marketPrices: [],
    isLoading: true,
    fromMarketDate: null,
    toMarketDate: null,
    fromDate: null,
    toDate: null,
    termScheduleId: null,
    originalQualityAttributeValues: null,
    filteredProduct: null,
    priceBasisId: null,
    currencyId: null,
    uomId: null,
    pricePrecision: 2,
    volumePrecision: 0,
    currencySymbol: "$",
  };

  async componentDidMount() {
    const { productId, market } = this.props;
    const { marketId } = market;

    const { fromMarketDate, toMarketDate } = this.getDefaultDateRange();

    const venue = await getVenue(market.venueId);
    const { originalQualityAttributeValues, filteredProduct } =
      await getFilteredProductForMarket(productId, marketId, toMarketDate);

    const termSchedule = await getTermSchedule(market.termScheduleId);

    const timeZone =
      venue.timeZone +
      " (" +
      Intl.DateTimeFormat("us", {
        timeZoneName: "short",
        timeZone: venue.timeZone,
      })
        .formatToParts()
        .find((i) => i.type === "timeZoneName").value +
      ")";

    this.setState({
      fromMarketDate: fromMarketDate,
      toMarketDate: toMarketDate,
      originalQualityAttributeValues: originalQualityAttributeValues,
      filteredProduct: filteredProduct,
      marketAttributes: market.marketAttributes,
      termScheduleId: market.termScheduleId,
      forwardReportingPeriodicity: termSchedule.forwardReportingPeriodicity,
      historicalReportingPeriodicity:
        termSchedule.historicalReportingPeriodicity,
      priceBasisId: market.marketAttributes[0].priceBasisId,
      currencyId: market.marketAttributes[0].currencyId,
      uomId: market.marketAttributes[0].uomId,
      pricePrecision: market.marketAttributes[0].pricePrecision ?? 2,
      volumePrecision: market.marketAttributes[0].volumePrecision ?? 0,
      currencySymbol: market.marketAttributes[0].currencySymbol ?? "$",
      timeZone: timeZone,
      isLoading: false,
    });

    fromMarketDate &&
      toMarketDate &&
      this.props.market &&
      termSchedule &&
      (await this.loadData(
        fromMarketDate,
        toMarketDate
      ));
  }

  getDefaultDateRange() {
    const today = new Date();
    const fromDate = new Date(today.getFullYear(), today.getMonth(), 1);

    return {
      fromMarketDate: fromDate.toLocaleDateString("fr-CA"),
      toMarketDate: today.toLocaleDateString("fr-CA"),
    };
  }

  parseMarketDate(marketDate) {
    const date = new Date(marketDate);
    date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
    return date;
  }

  async loadData(fromMarketDate, toMarketDate) {
    const fromDate = this.parseMarketDate(fromMarketDate);
    const toDate = this.parseMarketDate(toMarketDate);

    this.setState({ fromDate: fromDate, toDate: toDate });

    const { market } = this.props;
    const { marketId } = market;

    await this.loadTrades(marketId, fromDate, toDate);
  }

  async loadTrades(marketId, fromDate, toDate) {
    const trades = await getAggregateTradesByMarket(
      marketId,
      fromDate.toLocaleDateString("fr-CA"),
      toDate.toLocaleDateString("fr-CA")
    );

    this.setState({ trades: trades });
  }

  handleFromMarketDateChange = async (fromMarketDate) => {
    this.setState({ fromMarketDate: fromMarketDate });

    const { toMarketDate } = this.state;
    await this.handleDateRangeChange(fromMarketDate, toMarketDate);
  };

  handleToMarketDateChange = async (toMarketDate) => {
    this.setState({ toMarketDate: toMarketDate });

    const { fromMarketDate } = this.state;
    await this.handleDateRangeChange(fromMarketDate, toMarketDate);
  };

  handleDateRangeChange = async (fromMarketDate, toMarketDate) => {
    const { market } = this.props;
    const { marketId } = market;

    if (!fromMarketDate || !toMarketDate) {
      return;
    }

    // Perform refiltering asynchronously in background
    refilterProductForMarketDateChange(this, toMarketDate, marketId);

    // Load data asynchronously (existing behavior)
    (async () => {
      await this.loadData(
        fromMarketDate,
        toMarketDate
      );
    })();
  };

  handlemarketAttributeSelect = (marketAttribute) => {
    this.setState({
      priceBasisId: marketAttribute.priceBasisId,
      currencyId: marketAttribute.currencyId,
      uomId: marketAttribute.uomId,
    });
  };

  getFilteredTrades() {
    const { trades, priceBasisId, uomId, currencyId } = this.state;

    const filteredTrades = trades.filter(
      (x) =>
        x.priceBasisId === priceBasisId &&
        x.uomId === uomId &&
        x.currencyId === currencyId
    );

    return { trades: filteredTrades };
  }

  render() {
    const {
      timeZone,
      marketAttributes,
      termScheduleId,
      historicalReportingPeriodicity,
      isLoading,
      toDate,
      fromDate,
      fromMarketDate,
      toMarketDate,
      priceBasisId,
      currencyId,
      uomId,
      pricePrecision,
      volumePrecision,
      currencySymbol,
      filteredProduct,
    } = this.state;

    const product = filteredProduct;
    const { trades } = this.getFilteredTrades();

    if (isLoading) return <Loading />;

    const { t, market } = this.props;
    const { marketId } = market;
    const marketName = (market?.name ?? market?.marketName ?? "").trim();
    const renderSectionTitle = (label) => (
      <span className="section-title-label">
        {label}
      </span>
    );

    const endDate = new Date().toLocaleDateString("fr-CA");

    return (
      <div className="container-fluid market-data-page">
        {marketName && <h1 className="market-name-page-title">{marketName}</h1>}
        <div className="row">
          <div className="col-md-3"></div>
          <div className="col-md-6">
            <div className="selector-title-class">{t("PriceBasis")}</div>
            <ListGroup
              value={marketAttributes[0]}
              horizontal="sm"
              className="price-basis-selector"
            >
              {marketAttributes.map((attribute, index) => (
                <ListGroup.Item
                  className="market-attribute-selector"
                  key={index}
                  action
                  active={priceBasisId === attribute.priceBasisId}
                  onClick={() => this.handlemarketAttributeSelect(attribute)}
                >
                  {attribute.priceBasisName +
                    " (" +
                    attribute.currencyName +
                    "/" +
                    attribute.uomName +
                    ")"}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
          <div className="col-md-3"></div>
        </div>
        <div className="row align-items-end">
          <div className="col-auto">
            <Input
              type="date"
              max={toMarketDate}
              onChange={(e) =>
                this.handleFromMarketDateChange(e.currentTarget.value)
              }
              value={fromMarketDate}
              label={
                <span className="selector-title-class">{t("FromDate")}</span>
              }
            />
          </div>
          <div className="col-auto">
            <Input
              type="date"
              min={fromMarketDate}
              max={endDate}
              onChange={(e) =>
                this.handleToMarketDateChange(e.currentTarget.value)
              }
              value={toMarketDate}
              label={
                <span className="selector-title-class">{t("ToDate")}</span>
              }
            />
          </div>
          <div className="col"></div>
          <div className="col-auto align-self-start time-zone-block">
            <div className="selector-title-class time-zone-title">
              {t("TimeZone")}
            </div>
            <div>{timeZone}</div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12 tile section-tile shadow-card">
            <h5 className="section-title">
              {renderSectionTitle(t("TradeHistory"))}
            </h5>
            <HistoricalTradeDataTable
              trades={trades}
              product={product}
              marketId={marketId}
              marketDate={toMarketDate}
              termScheduleId={termScheduleId}
              historicalReportingPeriodicity={historicalReportingPeriodicity}
              fromDate={fromDate}
              toDate={toDate}
              priceBasisId={priceBasisId}
              currencyId={currencyId}
              uomId={uomId}
              pricePrecision={pricePrecision}
              volumePrecision={volumePrecision}
              currencySymbol={currencySymbol}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default withTranslation(["marketdata"])(HistoricalTradeDataHeader);
